import React, { useState, useRef } from 'react';
import { X, Printer, Download, MessageCircle, Loader2, FileText } from 'lucide-react';
import { toast } from 'react-toastify';

/**
 * ReportPreviewModal Component
 * Displays a report preview with Print, Download, and WhatsApp actions
 * Supports: sales-book, pl-consolidated, inventory-valuation, pl-branch
 */
const ReportPreviewModal = ({ 
  isOpen, 
  onClose, 
  title,
  subtitle,
  children,
  onDownload,
  onWhatsApp,
  downloading = false,
  sendingWhatsApp = false
}) => {
  const [printing, setPrinting] = useState(false);
  const contentRef = useRef(null);

  if (!isOpen) return null;

  const handlePrint = () => {
    if (!contentRef.current) return;
    
    setPrinting(true);
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${title}</title>
            <style>
              /* A4 Paper: 210mm x 297mm */
              @page { 
                size: A4 landscape;
                margin: 10mm;
              }
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: Arial, Helvetica, sans-serif;
                font-size: 9pt;
                color: #333;
                width: 277mm; /* A4 landscape minus margins */
                margin: 0 auto;
              }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin: 8px 0;
                font-size: 8pt;
              }
              th, td { 
                border: 0.5pt solid #999; 
                padding: 4px 6px; 
                text-align: left;
              }
              th { 
                background: #e5e5e5; 
                font-weight: bold;
                font-size: 8pt;
              }
              .report-header {
                text-align: center;
                margin-bottom: 15px;
                padding-bottom: 10px;
                border-bottom: 1.5pt solid #333;
              }
              .report-header h1 {
                font-size: 16pt;
                margin-bottom: 3px;
              }
              .report-header p {
                color: #666;
                font-size: 10pt;
              }
              .summary-grid {
                display: flex;
                justify-content: space-between;
                margin-bottom: 10px;
              }
              .summary-card {
                display: inline-block;
                padding: 6px 12px;
                margin: 3px;
                background: #f5f5f5;
                border: 0.5pt solid #ddd;
                border-radius: 4px;
                text-align: center;
              }
              .summary-card p { margin: 2px 0; }
              .text-right { text-align: right; }
              .text-center { text-align: center; }
              .font-bold { font-weight: bold; }
              .text-green { color: #059669; }
              .text-red { color: #dc2626; }
              .total-row { 
                background: #e5e5e5; 
                font-weight: bold;
              }
              .page-break { page-break-before: always; }
              h3 { font-size: 11pt; margin: 12px 0 8px 0; }
              .footer {
                margin-top: 15px;
                padding-top: 8px;
                border-top: 0.5pt solid #999;
                font-size: 7pt;
                color: #666;
                text-align: center;
              }
              @media print {
                body { 
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>${title}</h1>
              <p>${subtitle || ''}</p>
            </div>
            ${contentRef.current.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      
      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        setPrinting(false);
      }, 250);
    } else {
      setPrinting(false);
      toast.error('Unable to open print window');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent-gold/10 rounded-lg">
              <FileText className="w-5 h-5 text-accent-gold" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-primary-black">{title}</h2>
              {subtitle && (
                <p className="text-sm text-secondary-gray">{subtitle}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Modal Body - Report Preview */}
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          <div 
            ref={contentRef}
            className="bg-white rounded-lg shadow-sm p-6 min-h-64"
          >
            {children}
          </div>
        </div>

        {/* Modal Footer - Action Buttons */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="flex flex-wrap justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            
            {onWhatsApp && (
              <button
                onClick={onWhatsApp}
                disabled={sendingWhatsApp}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {sendingWhatsApp ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <MessageCircle className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">WhatsApp</span>
              </button>
            )}
            
            <button
              onClick={handlePrint}
              disabled={printing}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {printing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Printer className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">Print</span>
            </button>
            
            {onDownload && (
              <button
                onClick={onDownload}
                disabled={downloading}
                className="flex items-center gap-2 px-4 py-2.5 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">Download PDF</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default ReportPreviewModal;
